import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Archive, LogOut, Save } from 'lucide-react';
import { supabase } from '../services/supabase';
import InviteManager from './InviteManager';
import PhotoPicker from './PhotoPicker';
import { useI18n, SUPPORTED_LANGUAGES } from '../i18n';
import { exec } from '../utils';
import { uploadAvatar, removeAvatar, profileAvatarPath } from '../lib/images';
import { Avatar, Badge, Button, Card, Field, Input, Notice, PageHeader, SectionTitle, Segmented, Select, Textarea } from './ui';

export default function Config({ session, projetoId, perfil: perfilGlobal, onPerfilChange, onProjetoChange, onArchive, onLogout }) {
  const { t, lang, setLang } = useI18n();
  const userId = session.user.id;
  const projetoSalvo = useRef(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fotoBusy, setFotoBusy] = useState(false);
  const [perfil, setPerfil] = useState({ nome: perfilGlobal?.nome || '', avatar_url: perfilGlobal?.avatar_url || null });
  const [projetoLocal, setProjetoLocal] = useState(null);
  const [membros, setMembros] = useState([]);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [activeTab, setActiveTab] = useState('perfil');
  const [membrosError, setMembrosError] = useState(false);

  const carregarDadosConfig = useCallback(async () => {
    setLoadingConfig(true);
    setMembrosError(false);
    try {
      const { data: perfilData } = await supabase.from('perfis').select('*').eq('id', userId).maybeSingle();
      if (perfilData) setPerfil(perfilData);

      const { data: projetoData } = await supabase.from('projetos').select('*').eq('id', projetoId).maybeSingle();
      if (projetoData) {
        setProjetoLocal(projetoData);
        projetoSalvo.current = projetoData;
      }

      const { data: membrosData, error: membrosErr } = await supabase.rpc('get_membros_projeto', { p_projeto_id: projetoId });
      if (membrosErr) {
        console.error('Error loading project members:', membrosErr);
        setMembrosError(true);
      } else {
        setMembros(membrosData || []);
      }
    } catch (error) {
      console.error('Error loading config data:', error);
      setMembrosError(true);
    } finally {
      setLoadingConfig(false);
    }
  }, [userId, projetoId]);

  useEffect(() => {
    carregarDadosConfig();
  }, [carregarDadosConfig]);

  const showMessage = (text, error = false, timeout = 3000) => {
    setMessage(text);
    setIsError(error);
    if (timeout) setTimeout(() => setMessage(''), timeout);
  };

  const salvarPerfil = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { error } = await supabase.from('perfis').upsert({ id: userId, nome: perfil.nome, updated_at: new Date().toISOString() });
      if (error) throw error;
      onPerfilChange({ nome: perfil.nome });
      showMessage(t('profile.profileUpdated'), false);
    } catch (error) {
      showMessage(t('profile.saveProfileError', { message: error.message }), true);
    } finally {
      setSaving(false);
    }
  };

  const salvarFotoPerfil = async (file) => {
    setFotoBusy(true);
    try {
      const avatar_url = await uploadAvatar(supabase, profileAvatarPath(userId), file);
      const { error } = await supabase.from('perfis').upsert({ id: userId, avatar_url, updated_at: new Date().toISOString() });
      if (error) throw error;
      setPerfil(prev => ({ ...prev, avatar_url }));
      onPerfilChange({ avatar_url });
      showMessage(t('photo.saved'), false);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      showMessage(t('errors.photoFailed'), true);
    } finally {
      setFotoBusy(false);
    }
  };

  const removerFotoPerfil = async () => {
    setFotoBusy(true);
    try {
      await removeAvatar(supabase, profileAvatarPath(userId)).catch(() => {});
      const { error } = await supabase.from('perfis').upsert({ id: userId, avatar_url: null, updated_at: new Date().toISOString() });
      if (error) throw error;
      setPerfil(prev => ({ ...prev, avatar_url: null }));
      onPerfilChange({ avatar_url: null });
    } catch (error) {
      console.error('Error removing profile photo:', error);
      showMessage(t('errors.photoFailed'), true);
    } finally {
      setFotoBusy(false);
    }
  };

  const atualizarProjeto = async (campo, valor) => {
    const salvo = projetoSalvo.current?.[campo] ?? '';
    if (valor === salvo) return;
    if (campo === 'nome' && !valor.trim()) {
      setProjetoLocal(prev => ({ ...prev, nome: salvo }));
      return;
    }
    try {
      const { error } = await exec(supabase.from('projetos').update({ [campo]: valor }).eq('id', projetoLocal.id).select('id').single());
      if (error) throw error;

      projetoSalvo.current = { ...projetoSalvo.current, [campo]: valor };
      onProjetoChange({ [campo]: valor });

      showMessage(t('config.savedMessage'), false, 2000);
    } catch (error) {
      showMessage(t('profile.updateError', { message: error.message }), true);
    }
  };

  const salvarGrupo = async () => {
    try {
      const mudancas = {
        finalidade: projetoLocal.finalidade,
        finalidade_outro: projetoLocal.finalidade_outro || null,
        tem_filhos: projetoLocal.tem_filhos,
        tem_animais: projetoLocal.tem_animais
      };
      const { error } = await exec(supabase.from('projetos').update(mudancas).eq('id', projetoLocal.id).select('id').single());

      if (error) throw error;

      projetoSalvo.current = { ...projetoSalvo.current, ...mudancas };
      onProjetoChange(mudancas);

      showMessage(t('config.groupSavedMessage'), false);
    } catch (error) {
      showMessage(t('profile.updateError', { message: error.message }), true);
    }
  };

  const removerMembroConfig = async (alvoId) => {
    if (alvoId === projetoLocal?.proprietario_id) {
      showMessage(t('profile.cannotRemoveOwner'), true);
      return;
    }
    if (!window.confirm(t('profile.confirmRemoveUser'))) return;
    try {
      const { error, count } = await exec(supabase.from('usuarios_projetos').delete({ count: 'exact' }).eq('projeto_id', projetoLocal.id).eq('user_id', alvoId));
      if (error) throw error;
      if (count === 0) throw new Error('No rows were deleted.');
      setMembros(membros.filter(m => m.user_id !== alvoId));
      showMessage(t('profile.userRemoved'), false);
    } catch (error) {
      showMessage(t('profile.removeError', { message: error.message }), true);
    }
  };

  const eProprietario = projetoLocal?.proprietario_id === userId;

  if (loadingConfig) {
    return (
      <div className="text-center py-20">
        <p className="text-sm font-medium text-gray-500">{t('common.loading')}</p>
      </div>
    );
  }

  const tabs = [
    ['perfil', t('config.tabProfile')],
    ['projeto', t('config.tabProject')],
    ['convites', t('config.tabInvites')],
  ];

  return (
    <div>
      <PageHeader title={t('config.title')} />

      <Segmented role="tablist" value={activeTab} onChange={setActiveTab} options={tabs} size="md" className="mb-5" />

      {message && (
        <Notice tone={isError ? 'error' : 'success'} className="mb-5">{message}</Notice>
      )}

      <div className="space-y-5">
        {activeTab === 'perfil' && (
          <Card className="p-4 sm:p-6 space-y-5">
            <PhotoPicker
              src={perfil.avatar_url}
              name={perfil.nome || session.user.email}
              color="#6A51A3"
              busy={fotoBusy}
              onSelect={salvarFotoPerfil}
              onRemove={removerFotoPerfil}
            />
            <Field label={t('config.nameLabel')} htmlFor="config-nome">
              <Input id="config-nome" type="text" value={perfil.nome || ''} onChange={(e) => setPerfil({ ...perfil, nome: e.target.value })} placeholder={t('config.namePlaceholder')} />
            </Field>
            <Field label={t('profile.emailLabel')}>
              <div className="h-9 px-3 flex items-center bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600">{session.user.email}</div>
            </Field>
            <Field label={t('config.languageLabel')} htmlFor="config-idioma" help={t('config.languageHelp')}>
              <Select id="config-idioma" value={lang} onChange={(e) => setLang(e.target.value)}>
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </Select>
            </Field>
            <div className="flex justify-end">
              <Button onClick={salvarPerfil} disabled={saving}>
                <Save size={16} />{saving ? t('common.saving') : t('config.saveProfile')}
              </Button>
            </div>
          </Card>
        )}

        {activeTab === 'projeto' && projetoLocal && (
          <>
            <Card className="p-4 sm:p-6 space-y-5">
              <Field label={t('config.projectNameLabel')} htmlFor="config-projeto-nome">
                <Input id="config-projeto-nome" type="text" value={projetoLocal.nome || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, nome: e.target.value })} onBlur={(e) => eProprietario && atualizarProjeto('nome', e.target.value)} disabled={!eProprietario} />
              </Field>
              <Field label={t('config.descriptionLabel')} htmlFor="config-projeto-descricao">
                <Textarea id="config-projeto-descricao" value={projetoLocal.descricao || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, descricao: e.target.value })} onBlur={(e) => eProprietario && atualizarProjeto('descricao', e.target.value)} disabled={!eProprietario} rows={3} placeholder={t('profile.descriptionPlaceholder')} />
              </Field>
            </Card>

            <Card className="p-4 sm:p-6">
              <SectionTitle>{t('config.membersTitle', { count: membros.length })}</SectionTitle>
              <p className="text-sm text-gray-500 mb-4 -mt-1">{t('config.membersSubtitle')}</p>
              {membrosError && (
                <Notice tone="error" className="mb-3" action={<Button size="sm" variant="secondary" onClick={carregarDadosConfig}>{t('common.retry')}</Button>}>
                  {t('config.membersLoadError')}
                </Notice>
              )}
              <div className="divide-y divide-gray-100">
                {membros.map((membro) => (
                  <div key={membro.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Avatar src={membro.usuario_avatar} name={membro.usuario_nome || membro.usuario_email} color="#6A51A3" size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-sm text-gray-900 truncate">{membro.usuario_nome || (membro.usuario_email || '').split('@')[0]}</span>
                          {membro.e_proprietario && <Badge tone="orange">{t('profile.ownerBadge')}</Badge>}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{membro.usuario_email}</div>
                      </div>
                    </div>
                    {eProprietario && !membro.e_proprietario && (
                      <Button variant="danger" size="sm" onClick={() => removerMembroConfig(membro.user_id)} className="w-full sm:w-auto">
                        {t('profile.removeMember')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4 sm:p-6">
              <SectionTitle>{t('config.groupInfoTitle')}</SectionTitle>
              <p className="text-sm text-gray-500 mb-4 -mt-1">{t('config.groupInfoSubtitle')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('config.purposeLabel')} htmlFor="config-finalidade" className={projetoLocal.finalidade === 'outro' ? '' : 'sm:col-span-2'}>
                  <Select id="config-finalidade" value={projetoLocal.finalidade || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, finalidade: e.target.value })} disabled={!eProprietario}>
                    <option value="">{t('common.select')}</option>
                    <option value="casal">{t('createProject.purposeCouple')}</option>
                    <option value="colegas_quarto">{t('createProject.purposeRoommates')}</option>
                    <option value="amigos">{t('createProject.purposeFriends')}</option>
                    <option value="trabalho">{t('createProject.purposeWork')}</option>
                    <option value="individual">{t('createProject.purposeIndividual')}</option>
                    <option value="outro">{t('createProject.purposeOther')}</option>
                  </Select>
                </Field>
                {projetoLocal.finalidade === 'outro' && (
                  <Field label={t('config.purposeOtherLabel')} htmlFor="config-finalidade-outro">
                    <Input id="config-finalidade-outro" type="text" value={projetoLocal.finalidade_outro || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, finalidade_outro: e.target.value })} disabled={!eProprietario} placeholder={t('createProject.purposeOtherPlaceholder')} />
                  </Field>
                )}
                <Field label={t('config.kidsLabel')} htmlFor="config-filhos">
                  <Select id="config-filhos" value={projetoLocal.tem_filhos || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, tem_filhos: e.target.value })} disabled={!eProprietario}>
                    <option value="">{t('common.select')}</option>
                    <option value="sim">{t('common.yes')}</option>
                    <option value="nao">{t('common.no')}</option>
                    <option value="prefiro_nao_responder">{t('common.preferNotToSay')}</option>
                  </Select>
                </Field>
                <Field label={t('config.petsLabel')} htmlFor="config-animais">
                  <Select id="config-animais" value={projetoLocal.tem_animais || ''} onChange={(e) => setProjetoLocal({ ...projetoLocal, tem_animais: e.target.value })} disabled={!eProprietario}>
                    <option value="">{t('common.select')}</option>
                    <option value="sim">{t('common.yes')}</option>
                    <option value="nao">{t('common.no')}</option>
                    <option value="prefiro_nao_responder">{t('common.preferNotToSay')}</option>
                  </Select>
                </Field>
              </div>
              {eProprietario && (
                <div className="flex justify-end mt-4">
                  <Button onClick={salvarGrupo}><Save size={16} />{t('config.saveGroupInfo')}</Button>
                </div>
              )}
            </Card>

            {eProprietario && (
              <Card className="p-4 sm:p-6 border-red-200">
                <SectionTitle>{t('config.archiveTitle')}</SectionTitle>
                <p className="text-sm text-gray-500 mb-4 -mt-1">{t('config.archiveBody')}</p>
                <Button variant="danger" onClick={onArchive}><Archive size={16} />{t('config.archiveButton')}</Button>
              </Card>
            )}
          </>
        )}

        {activeTab === 'convites' && (
          <Card className="p-4 sm:p-6">
            {!eProprietario ? (
              <p className="text-sm text-gray-500 text-center py-10">{t('config.ownerOnlyInvites')}</p>
            ) : projetoLocal?.id ? (
              <InviteManager projetoId={projetoLocal.id} />
            ) : (
              <p className="text-sm text-gray-500 text-center py-10">{t('config.projectLoading')}</p>
            )}
          </Card>
        )}

        <Button variant="secondary" onClick={onLogout} className="w-full sm:w-auto">
          <LogOut size={16} />{t('config.logout')}
        </Button>
      </div>
    </div>
  );
}
